import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Typography,
    TextField,
    Autocomplete,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    asyncCreateQuestion,
    asyncUpdateQuestion,
} from '../../../../../../../services/survey.service';
import { ToastSwal } from '../../../../../../components/general/toastSwals/toastSwal';
import FieldText from '../../../../../../components/general/fields/fieldText';
import LoadingButtonGeneral from '../../../../../../components/general/buttons/loadingButtonGeneral';

interface Props {
    open: boolean;
    onClose: (reload?: boolean) => void;
    surveyId: string;
    question?: any;
    questionsDependency?: any[];
}

const questionTypes = [
    { value: 'text', label: 'Texto' },
    { value: 'date', label: 'Fecha' },
    { value: 'single', label: 'Opción única' },
    { value: 'multiple', label: 'Opción múltiple' },
    { value: 'linear', label: 'Escala lineal' },
];

const questionSubtypes = [
    { value: 'short', label: 'Respuesta corta' },
    { value: 'long', label: 'Respuesta larga' },
];

const typesSupported: { [key: string]: boolean } = {
    linear: true,
    single: true,
    multiple: true,
    'list-only': true,
    'linear-label': true,
};

export const ModalQuestion = ({
    open,
    onClose,
    surveyId,
    question,
    questionsDependency,
}: Props) => {
    const [loading, setLoading] = useState(false);
    const [dependencies, setDependencies] = useState<any[]>([]);
    const [selectedDependency, setSelectedDependency] = useState<any>(null);

    const validationSchema = Yup.object({
        description: Yup.string()
            .required('La descripción es requerida')
            .test(
                'unique-description',
                'Ya existe una pregunta con esta descripción',
                function (value) {
                    if (!value) return true;
                    // Verificar si hay otra pregunta con la misma descripción
                    const isDuplicate = questionsDependency?.some(
                        (q: any) =>
                            q.description.toLowerCase().trim() ===
                                value.toLowerCase().trim() &&
                            q.id !== question?.id
                    );
                    return !isDuplicate;
                }
            ),
        type: Yup.string().required('El tipo es requerido'),
        subtype: Yup.string().when('type', {
            is: 'text',
            then: (schema) => schema.required('El subtipo es requerido'),
        }),
        questionDependency: Yup.string().when('dependency', {
            is: true,
            then: (schema) => schema.required('Debe seleccionar una pregunta'),
        }),
        answerDependency: Yup.string().when('dependency', {
            is: (val: boolean) =>
                val &&
                selectedDependency &&
                selectedDependency.type !== 'linear',
            then: (schema) => schema.required('Debe seleccionar una respuesta'),
        }),
        range1: Yup.number().when('dependency', {
            is: (val: boolean) =>
                val &&
                selectedDependency &&
                selectedDependency.type === 'linear',
            then: (schema) =>
                schema
                    .required('El rango es obligatorio')
                    .typeError('Debe ser un número'),
        }),
        range2: Yup.number().when('dependency', {
            is: (val: boolean) =>
                val &&
                selectedDependency &&
                selectedDependency.type === 'linear',
            then: (schema) =>
                schema
                    .required('El rango es obligatorio')
                    .typeError('Debe ser un número'),
        }),
    });

    const formik = useFormik({
        initialValues: {
            surveyId: surveyId,
            description: question?.description || '',
            type: question?.type || 'text',
            subtype: question?.subtype || '',
            icon: question?.icon || '',
            status: 'active',
            why: question?.why || false,
            required: question?.required || false,
            dependency: question?.dependency || false,
            position: question?.position || 0,
            questionDependency: '',
            answerDependency: '',
            range1: '',
            range2: '',
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setLoading(true);
            try {
                // Preparar datos de dependencia
                const payload: any = { ...values };

                if (values.dependency && selectedDependency) {
                    if (selectedDependency.type === 'linear') {
                        // Para tipo linear, guardar el rango como answerDependency
                        payload.answerDependency = `${values.range1}-${values.range2}`;
                    }
                } else {
                    // Si no hay dependencia, limpiar campos relacionados
                    delete payload.questionDependency;
                    delete payload.answerDependency;
                    delete payload.range1;
                    delete payload.range2;
                }

                if (question?.id) {
                    await asyncUpdateQuestion(question.id, payload);
                    ToastSwal.fire({
                        text: 'Pregunta actualizada exitosamente',
                        icon: 'success',
                        timer: 3000,
                        position: 'center-end',
                    });
                } else {
                    await asyncCreateQuestion(payload);
                    ToastSwal.fire({
                        text: 'Pregunta creada exitosamente',
                        icon: 'success',
                        timer: 3000,
                        position: 'center-end',
                    });
                }
                onClose(true);
            } catch (error) {
                ToastSwal.fire({
                    text: 'Error al guardar la pregunta',
                    icon: 'error',
                    timer: 3000,
                    position: 'center-end',
                });
            } finally {
                setLoading(false);
            }
        },
    });

    // Cargar dependencias disponibles
    useEffect(() => {
        if (questionsDependency) {
            if (question) {
                // Al editar: filtrar preguntas que aparecen antes
                // Usar tanto el índice del array como la posición de la BD
                const currentIndex = questionsDependency.findIndex(
                    (q: any) => q.id === question.id
                );

                const availableDeps = questionsDependency.filter(
                    (q: any, index: number) => {
                        // Una pregunta es válida como dependencia si:
                        // 1. Está antes en el array (índice menor) O tiene posición menor en BD
                        // 2. Es de un tipo soportado
                        // 3. Está activa
                        const isBeforeInArray = index < currentIndex;
                        const isBeforeInPosition =
                            q.position < question.position;

                        return (
                            (isBeforeInArray || isBeforeInPosition) &&
                            typesSupported[q.type] &&
                            q.status === 'active'
                        );
                    }
                );
                setDependencies(availableDeps);
            } else {
                // Al crear: todas las preguntas activas pueden ser dependencias
                const availableDeps = questionsDependency.filter(
                    (q: any) => typesSupported[q.type] && q.status === 'active'
                );
                setDependencies(availableDeps);
            }
        } else {
            setDependencies([]);
        }
    }, [questionsDependency, question]);

    // Cargar datos de dependencia al editar
    useEffect(() => {
        if (
            question &&
            question.questionDependency &&
            question.questionDependency.length > 0 &&
            dependencies.length > 0
        ) {
            const dep = question.questionDependency[0];
            const depQuestion = dependencies.find(
                (d: any) => d.id === dep.questionDependency?.id
            );

            if (depQuestion) {
                setSelectedDependency(depQuestion);
                formik.setFieldValue('questionDependency', depQuestion.id);

                if (depQuestion.type === 'linear') {
                    const [range1, range2] = (
                        dep.answerTextDependency || ''
                    ).split('-');
                    formik.setFieldValue('range1', range1 || '');
                    formik.setFieldValue('range2', range2 || '');
                } else {
                    formik.setFieldValue(
                        'answerDependency',
                        dep.answerDependency?.id || ''
                    );
                }
            }
        }
    }, [dependencies]);

    // Actualizar dependencia seleccionada cuando cambia
    const handleDependencyChange = (event: any, value: any) => {
        setSelectedDependency(value);
        formik.setFieldValue('questionDependency', value?.id || '');
        // Limpiar campos de respuesta/rango
        formik.setFieldValue('answerDependency', '');
        formik.setFieldValue('range1', '');
        formik.setFieldValue('range2', '');
    };

    return (
        <Dialog open={open} onClose={() => onClose()} maxWidth="md" fullWidth>
            <DialogTitle>
                <Typography
                    sx={{
                        fontSize: { lg: 27, xs: 23 },
                        mt: { xs: 1.5, sm: 1.5, md: 1.5, lg: 0 },
                        color: 'primary.main',
                    }}
                >
                    {question ? 'Editar Pregunta' : 'Nueva Pregunta'}
                </Typography>
            </DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FieldText
                                label="Descripción de la pregunta"
                                name="description"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                touched={formik.touched.description}
                                errors={formik.errors.description}
                                rows={2}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                size="small"
                                label="Tipo de pregunta"
                                name="type"
                                value={formik.values.type}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={
                                    formik.touched.type &&
                                    Boolean(formik.errors.type)
                                }
                                helperText={
                                    formik.touched.type && formik.errors.type
                                        ? String(formik.errors.type)
                                        : ''
                                }
                                required
                                sx={{
                                    '& label.Mui-focused': {
                                        color: 'primary.main',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                }}
                            >
                                {questionTypes.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {formik.values.type === 'text' && (
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    select
                                    size="small"
                                    label="Subtipo"
                                    name="subtype"
                                    value={formik.values.subtype}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.subtype &&
                                        Boolean(formik.errors.subtype)
                                    }
                                    helperText={
                                        formik.touched.subtype &&
                                        formik.errors.subtype
                                            ? String(formik.errors.subtype)
                                            : ''
                                    }
                                    required
                                    sx={{
                                        '& label.Mui-focused': {
                                            color: 'primary.main',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'primary.main',
                                            },
                                        },
                                    }}
                                >
                                    {questionSubtypes.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="required"
                                        checked={formik.values.required}
                                        onChange={formik.handleChange}
                                        sx={{
                                            color: 'primary.main',
                                            '&.Mui-checked': {
                                                color: 'primary.main',
                                            },
                                        }}
                                    />
                                }
                                label="Pregunta requerida"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="why"
                                        checked={formik.values.why}
                                        onChange={formik.handleChange}
                                        sx={{
                                            color: 'primary.main',
                                            '&.Mui-checked': {
                                                color: 'primary.main',
                                            },
                                        }}
                                    />
                                }
                                label="Incluir campo '¿Por qué?'"
                            />
                        </Grid>

                        {(dependencies.length > 0 || question) && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="dependency"
                                            checked={formik.values.dependency}
                                            onChange={formik.handleChange}
                                            sx={{
                                                color: 'primary.main',
                                                '&.Mui-checked': {
                                                    color: 'primary.main',
                                                },
                                            }}
                                        />
                                    }
                                    label="Tiene dependencia"
                                />
                            </Grid>
                        )}

                        {formik.values.dependency && (
                            <>
                                {dependencies.length === 0 ? (
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontStyle: 'italic' }}
                                        >
                                            No hay preguntas disponibles para
                                            establecer dependencias. Las
                                            preguntas solo pueden depender de
                                            preguntas anteriores con tipo opción
                                            única, opción múltiple o escala
                                            lineal.
                                        </Typography>
                                    </Grid>
                                ) : (
                                    <>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                options={dependencies}
                                                getOptionLabel={(option: any) =>
                                                    option.description || ''
                                                }
                                                value={selectedDependency}
                                                onChange={
                                                    handleDependencyChange
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Pregunta a depender"
                                                        size="small"
                                                        required
                                                        error={
                                                            formik.touched
                                                                .questionDependency &&
                                                            Boolean(
                                                                formik.errors
                                                                    .questionDependency
                                                            )
                                                        }
                                                        helperText={
                                                            formik.touched
                                                                .questionDependency &&
                                                            formik.errors
                                                                .questionDependency
                                                                ? String(
                                                                    formik
                                                                        .errors
                                                                        .questionDependency
                                                                )
                                                                : ''
                                                        }
                                                        sx={{
                                                            '& label.Mui-focused':
                                                                {
                                                                    color: 'primary.main',
                                                                },
                                                            '& .MuiOutlinedInput-root':
                                                                {
                                                                    '&.Mui-focused fieldset':
                                                                        {
                                                                            borderColor:
                                                                                'primary.main',
                                                                        },
                                                                },
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        {selectedDependency &&
                                            selectedDependency.type ===
                                                'linear' && (
                                            <>
                                                <Grid item xs={6}>
                                                    <FieldText
                                                        label={`Rango desde (${
                                                            selectedDependency
                                                                .answers?.[0]
                                                                ?.range ||
                                                                '1-5'
                                                        })`}
                                                        name="range1"
                                                        value={
                                                            formik.values
                                                                .range1
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                        onBlur={
                                                            formik.handleBlur
                                                        }
                                                        touched={
                                                            formik.touched
                                                                .range1
                                                        }
                                                        errors={
                                                            formik.errors
                                                                .range1
                                                        }
                                                        required
                                                    />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <FieldText
                                                        label={`Rango hasta (${
                                                            selectedDependency
                                                                .answers?.[0]
                                                                ?.range ||
                                                                '1-5'
                                                        })`}
                                                        name="range2"
                                                        value={
                                                            formik.values
                                                                .range2
                                                        }
                                                        onChange={
                                                            formik.handleChange
                                                        }
                                                        onBlur={
                                                            formik.handleBlur
                                                        }
                                                        touched={
                                                            formik.touched
                                                                .range2
                                                        }
                                                        errors={
                                                            formik.errors
                                                                .range2
                                                        }
                                                        required
                                                    />
                                                </Grid>
                                            </>
                                        )}

                                        {selectedDependency &&
                                            [
                                                'single',
                                                'multiple',
                                                'linear-label',
                                                'list-only',
                                            ].includes(
                                                selectedDependency.type
                                            ) && (
                                            <>
                                                {!selectedDependency.answers ||
                                                    selectedDependency.answers
                                                        .length === 0 ? (
                                                        <Grid item xs={12}>
                                                            <Typography
                                                                variant="body2"
                                                                color="warning.main"
                                                                sx={{
                                                                    fontStyle:
                                                                        'italic',
                                                                }}
                                                            >
                                                                Esta pregunta no
                                                                tiene respuestas
                                                                configuradas.
                                                                Por favor,
                                                                primero agrega
                                                                respuestas a la
                                                                pregunta "
                                                                {
                                                                    selectedDependency.description
                                                                }
                                                                ".
                                                            </Typography>
                                                        </Grid>
                                                    ) : (
                                                        <Grid item xs={12}>
                                                            <Autocomplete
                                                                options={
                                                                    selectedDependency.answers
                                                                }
                                                                getOptionLabel={(
                                                                    option: any
                                                                ) =>
                                                                    selectedDependency.type ===
                                                                    'linear-label'
                                                                        ? option.label ||
                                                                          ''
                                                                        : option.description ||
                                                                          ''
                                                                }
                                                                value={
                                                                    selectedDependency.answers.find(
                                                                        (
                                                                            a: any
                                                                        ) =>
                                                                            a.id ===
                                                                            formik
                                                                                .values
                                                                                .answerDependency
                                                                    ) || null
                                                                }
                                                                onChange={(
                                                                    event,
                                                                    value
                                                                ) => {
                                                                    formik.setFieldValue(
                                                                        'answerDependency',
                                                                        value?.id ||
                                                                            ''
                                                                    );
                                                                }}
                                                                disabled={
                                                                    !formik
                                                                        .values
                                                                        .questionDependency
                                                                }
                                                                renderInput={(
                                                                    params
                                                                ) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Respuesta a depender"
                                                                        size="small"
                                                                        required
                                                                        error={
                                                                            formik
                                                                                .touched
                                                                                .answerDependency &&
                                                                            Boolean(
                                                                                formik
                                                                                    .errors
                                                                                    .answerDependency
                                                                            )
                                                                        }
                                                                        helperText={
                                                                            formik
                                                                                .touched
                                                                                .answerDependency &&
                                                                            formik
                                                                                .errors
                                                                                .answerDependency
                                                                                ? String(
                                                                                    formik
                                                                                        .errors
                                                                                        .answerDependency
                                                                                )
                                                                                : ''
                                                                        }
                                                                        sx={{
                                                                            '& label.Mui-focused':
                                                                                {
                                                                                    color: 'primary.main',
                                                                                },
                                                                            '& .MuiOutlinedInput-root':
                                                                                {
                                                                                    '&.Mui-focused fieldset':
                                                                                        {
                                                                                            borderColor:
                                                                                                'primary.main',
                                                                                        },
                                                                                },
                                                                        }}
                                                                    />
                                                                )}
                                                            />
                                                        </Grid>
                                                    )}
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <LoadingButtonGeneral
                        onClick={() => onClose()}
                        disabled={loading}
                        title="Cancelar"
                        variant="outlined"
                    />
                    <LoadingButtonGeneral
                        onClick={formik.handleSubmit}
                        loading={loading}
                        disabled={loading}
                        title="Guardar"
                    />
                </DialogActions>
            </form>
        </Dialog>
    );
};
